import React from 'react';
import PropTypes from 'prop-types';
import { Flex, Typography, Button } from 'antd';
import { SettingOutlined, HomeOutlined } from '@ant-design/icons';

const { Title } = Typography;

/**
 * HeaderTitleButtons
 *
 * Component that displays a title with an icon and up to two configurable buttons on the right.
 *
 * @param {Object} props - Component properties
 * @param {string} props.title - Title to display
 * @param {React.ReactNode} [props.titleIcon] - Optional icon for the title
 * @param {number} [props.titleLevel] - Title level (1-5)
 * @param {string} [props.button1Text] - First button text
 * @param {React.ReactNode} [props.button1Icon] - First button icon
 * @param {'default'|'primary'|'dashed'|'text'|'link'} [props.button1Type] - First button type
 * @param {function} [props.button1OnClick] - Function to execute on first button click
 * @param {string} [props.button2Text] - Second button text
 * @param {React.ReactNode} [props.button2Icon] - Second button icon
 * @param {'default'|'primary'|'dashed'|'text'|'link'} [props.button2Type] - Second button type
 * @param {function} [props.button2OnClick] - Function to execute on second button click
 * @param {boolean} [props.isButtonsShowed] - Whether to show the buttons
 */
export const HeaderTitleButtons = ({
  title,
  titleIcon,
  titleLevel,
  button1Text,
  button1Icon,
  button1Type,
  button1OnClick,
  button2Text,
  button2Icon,
  button2Type,
  button2OnClick,
  isButtonsShowed,
}) => {
  return (
    <Flex justify={title ? 'space-between' : 'flex-end'} gap={16}>
      {title && (
        <Title level={titleLevel}>
          {titleIcon || null} {title}
        </Title>
      )}
      {isButtonsShowed && (
        <Flex justify="space-between" align="flex-start" gap={16}>
          <Button size="large" type={button1Type || 'default'} icon={button1Icon} onClick={button1OnClick}>
            {button1Text}
          </Button>
          <Button size="large" type={button2Type || 'default'} icon={button2Icon} onClick={button2OnClick}>
            {button2Text}
          </Button>
        </Flex>
      )}
    </Flex>
  );
};

HeaderTitleButtons.propTypes = {
  title: PropTypes.string,
  titleIcon: PropTypes.node,
  titleLevel: PropTypes.number,
  button1Text: PropTypes.string,
  button1Icon: PropTypes.node,
  button1Type: PropTypes.oneOf(['default', 'primary', 'dashed', 'text', 'link']),
  button1OnClick: PropTypes.func,
  button2Text: PropTypes.string,
  button2Icon: PropTypes.node,
  button2Type: PropTypes.oneOf(['default', 'primary', 'dashed', 'text', 'link']),
  button2OnClick: PropTypes.func,
};

HeaderTitleButtons.defaultProps = {
  titleIcon: null,
  titleLevel: 1,
  button1Text: 'Title Header',
  button1Icon: <SettingOutlined />,
  button1Type: 'default',
  button1OnClick: () => {},
  button2Text: 'Button Left',
  button2Icon: <HomeOutlined />,
  button2Type: 'default',
  button2OnClick: () => {},
};
